import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  ZEITPUNKTNAME,
  fakturierungsliste,
  type Fakturierungszeile,
} from '../api/auftraege';
import { ApiFehler } from '../api/fehler';
import { AbTabletHinweis } from '../komponenten/AbTablet';
import { Meldung } from '../komponenten/Meldung';
import { useTelefon } from '../komponenten/telefon';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { alsEuro, alsMenge } from '../betrag';
import { alsTag } from '../datum';
import { Sammelrechnung } from './Sammelrechnung';

/**
 * Die Fakturierungsliste (AUF-20).
 *
 * „Nicht berechnete Positionen erscheinen in einer Fakturierungsliste mit
 * Fälligkeit."
 *
 * DIE FÄLLIGKEIT BLEIBT BEI „nach Durchführung" LEER, und die Maske sagt es.
 * Das Datum der Erbringung entsteht erst mit Kapitel 13; ein geschätztes wäre
 * schlimmer als keines — es sähe aus wie eine Auskunft, und die Mahnung liefe
 * danach.
 *
 * DIE ZEILEN OHNE FÄLLIGKEIT STEHEN UNTEN und nicht oben: Was fällig ist,
 * gehört an den Anfang der Liste. Der Server sortiert bereits so
 * (`NULLS LAST`); die Maske ordnet nicht um.
 *
 * Seitentyp Liste (ÄA-41): Zähler und offene Summe im Seitenkopf, der Schalter
 * „nur mit Fälligkeit" in der Filterleiste, die Tabelle in der Karte. Eine
 * Suche hat die Liste nicht — der Server liefert sie ungefiltert.
 *
 * DIE SAMMELRECHNUNG (RNG-06, Auftrag CRM-119): Wer Rechnungen anlegen darf,
 * wählt Zeilen EINES Kunden — auch aus mehreren Aufträgen — und legt daraus
 * einen Entwurf an. Ist ein Kunde gewählt, sind die Zeilen der anderen zu;
 * die API wiese sie ohnehin ab. Auswählen ab Tablet (ÄA-63).
 */
export function Fakturierungsliste({
  zurKunde,
  darfAnlegen = false,
  beiAuftrag,
  beiRechnung,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  /** `rechnung/anlegen`, wie die API. */
  readonly darfAnlegen?: boolean;
  readonly beiAuftrag: (id: string) => void;
  readonly beiRechnung?: (id: string) => void;
}): JSX.Element {
  const telefon = useTelefon();
  const waehlbar = darfAnlegen && beiRechnung !== undefined && !telefon;
  const [gewaehlt, setzeGewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [vorschau, setzeVorschau] = useState(false);
  const [zeilen, setzeZeilen] = useState<readonly Fakturierungszeile[]>([]);
  const [nurFaellige, setzeNurFaellige] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeZeilen(await fakturierungsliste());
      setzeGewaehlt(new Set());
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const gezeigt = nurFaellige ? zeilen.filter((z) => z.faelligAm !== null) : zeilen;
  const summe = gezeigt.reduce((bisher, z) => bisher + Number(z.offenerWert), 0);
  const ohneDatum = zeilen.filter((z) => z.faelligAm === null).length;
  const auswahl = zeilen.filter((z) => gewaehlt.has(z.positionId));
  /* RNG-06: EIN Kunde je Sammelrechnung — der der ersten gewählten Zeile. */
  const kundeDerAuswahl = auswahl[0]?.kunde;

  const umschalten = (position: string, an: boolean): void => {
    const neu = new Set(gewaehlt);
    if (an) neu.add(position);
    else neu.delete(position);
    setzeGewaehlt(neu);
  };

  const marken: readonly Filtermarke[] = nurFaellige
    ? [{ text: 'nur mit Fälligkeit', entfernen: () => setzeNurFaellige(false) }]
    : [];

  /* Die Summe als Text vom Server, aufsummiert wie bisher, dargestellt nach RND-05. */
  const offenGesamt = alsEuro(summe.toFixed(2));

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  const zaehler = zaehlwort(gezeigt.length, 'offene Position', 'offene Positionen');
  useZaehler(laedt || zaehler === undefined ? undefined : `${zaehler} · ${offenGesamt} offen`);

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <>
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={nurFaellige}
                onChange={(e) => setzeNurFaellige(e.target.checked)}
              />
              nur mit Fälligkeit
            </label>
            <button type="button" className="knopf" onClick={() => void laden()}>
              Neu laden
            </button>
            {waehlbar && (
              <button
                type="button"
                className="knopf knopf--haupt"
                disabled={auswahl.length === 0}
                onClick={() => setzeVorschau(true)}
              >
                Sammelrechnung …{auswahl.length > 0 && ` (${auswahl.length})`}
              </button>
            )}
          </>
        }
        marken={marken}
        hinweise={
          <>
            {/* AUF-21: der Fakturierungszeitpunkt bestimmt die Faelligkeit. */}
            <p className="hilfe">
              Alle bestätigten Aufträge mit noch nicht berechneten Positionen. Die Fälligkeit
              folgt dem vereinbarten Fakturierungszeitpunkt: bei Buchung das Belegdatum, zum
              vereinbarten Termin das vereinbarte Datum — und nach Durchführung das Datum der
              Erbringung, das erst mit der Durchführung entsteht.
            </p>

            <AbTabletHinweis an={darfAnlegen} />
            {waehlbar && (
              <p className="hilfe">
                Für eine Sammelrechnung die Positionen eines Kunden wählen — auch aus mehreren
                Aufträgen.
              </p>
            )}

            {ohneDatum > 0 && !nurFaellige && (
              <p className="warnung">
                {ohneDatum} {ohneDatum === 1 ? 'Position wartet' : 'Positionen warten'} auf das
                Datum der Erbringung. Sie stehen am Ende der Liste; ein geschätztes
                Fälligkeitsdatum wäre schlimmer als keines.
              </p>
            )}
          </>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">
              {gezeigt.length} offene Positionen · {offenGesamt} offen
            </caption>
            <thead>
              <tr>
                {waehlbar && (
                  <th scope="col">
                    <span className="nur-vorlesen">Auswahl</span>
                  </th>
                )}
                <th scope="col">Auftrag</th>
                <th scope="col">Kunde</th>
                <th scope="col">Position</th>
                <th scope="col" className="zahl">
                  offene Menge
                </th>
                <th scope="col" className="zahl">
                  offener Wert
                </th>
                <th scope="col">Zeitpunkt</th>
                <th scope="col">fällig</th>
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((z) => (
                <tr key={z.positionId}>
                  {waehlbar && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${z.auftragsnummer} · ${z.bezeichnung} für die Sammelrechnung wählen`}
                        checked={gewaehlt.has(z.positionId)}
                        disabled={kundeDerAuswahl !== undefined && z.kunde !== kundeDerAuswahl}
                        title={
                          kundeDerAuswahl !== undefined && z.kunde !== kundeDerAuswahl
                            ? 'Eine Sammelrechnung fasst nur Positionen eines Kunden zusammen.'
                            : undefined
                        }
                        onChange={(e) => umschalten(z.positionId, e.target.checked)}
                      />
                    </td>
                  )}
                  <td>
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiAuftrag(z.auftrag)}
                    >
                      {z.auftragsnummer}
                    </button>
                  </td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={z.kunde} zurAkte={zurKunde}>
                    {z.kundenname}
                  </KundeVerweis>
                </td>
                  <td>{z.bezeichnung}</td>
                  <td className="zahl">{alsMenge(z.offeneMenge)}</td>
                  <td className="zahl">{alsEuro(z.offenerWert)}</td>
                  <td>{ZEITPUNKTNAME[z.zeitpunkt]}</td>
                  <td>
                    {z.faelligAm === null ? (
                      <span className="feld__hilfe">nach Erbringung</span>
                    ) : (
                      <>
                        <span className="liste__einzeilig">{alsTag(z.faelligAm)}</span>
                        {z.ueberfaellig && (
                          <>
                            {' '}
                            <span className="marke marke--gesperrt">überfällig</span>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && gezeigt.length === 0 && (
          <div className="liste__leer" role="status">
            <p>Keine offene Position — alles ist berechnet.</p>
          </div>
        )}
      </Liste>

      {vorschau && beiRechnung !== undefined && (
        <Sammelrechnung
          zeilen={auswahl}
          beiSchliessen={() => setzeVorschau(false)}
          beiAngelegt={(rechnung) => {
            setzeVorschau(false);
            beiRechnung(rechnung);
          }}
        />
      )}
    </>
  );
}
